import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import { useMessage } from '../common/MessageDisplay';

const HomePage = () => {
  const { ErrorDisplay, SuccessDisplay, showError } = useMessage();
  const navigate = useNavigate();
  const [airports, setAirports] = useState([]);

  const [searchData, setSearchData] = useState({
    departureIataCode: '',
    arrivalIataCode: '',
    departureDate: '',
  });

  const popularDestinations = [
    { id: 1, city: 'New York', country: 'USA', price: '$450', image: 'usa.jpg' },
    { id: 2, city: 'London', country: 'UK', price: '$550', image: 'uk.jpg' },
    { id: 3, city: 'Dubai', country: 'UAE', price: '$600', image: 'uae.webp' },
    { id: 4, city: 'Tokyo', country: 'Japan', price: '$700', image: 'japan.webp' },
  ];

  //fetch all airports on component mount
  useEffect(() => {
    const fetchAirports = async () => {
      try {
        const response = await ApiService.getAllAirports();
        setAirports(response.data || []);
      } catch (error) {
        showError('Failed to load airports. Please try again later.');
      }
    };
    fetchAirports();
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchData.departureIataCode || !searchData.arrivalIataCode || !searchData.departureDate) {
      showError('Please fill in all search fields.');
      return;
    }
    navigate(
      `/flights?departureIataCode=${searchData.departureIataCode}&arrivalIataCode=${searchData.arrivalIataCode}&departureDate=${searchData.departureDate}`
    );
  };

  const handleSwapAirports = () => {
    setSearchData((prev) => ({
      ...prev,
      departureIataCode: prev.arrivalIataCode,
      arrivalIataCode: prev.departureIataCode,
    }));
  };

  const formatAiportOption = (airport) => {
    return `${airport.iataCode} (${airport.city}) - ${airport.name}, ${airport.country}`;
  };

  return (
    <div className="home-page">
      <div className="hero-section">
        <div className="hero-content">
          <h1>Book your flight with Daniel Airlines</h1>
          <p>Find the best deals on flights to your favorite destinations.</p>
        </div>

        <div className="search-box">
          <ErrorDisplay />
          <SuccessDisplay />

          <form onSubmit={handleSearch}>
            <div className="search-fields">
              <div className="form-group">
                <label htmlFor="">From</label>
                <select
                  required
                  value={searchData.departureIataCode}
                  onChange={(e) =>
                    setSearchData({ ...searchData, departureIataCode: e.target.value })
                  }
                >
                  <option value="">Select Departure Airport</option>
                  {airports.map((airport) => (
                    <option key={`dep-${airport.iataCode}`} value={airport.iataCode}>
                      {formatAiportOption(airport)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="swap-cities">
                <button
                  type="button"
                  onClick={handleSwapAirports}
                  aria-label="Swap Departure and Arrival"
                >
                  &#8646;
                </button>
              </div>

              <div className="form-group">
                <label htmlFor="">To</label>
                <select
                  value={searchData.arrivalIataCode}
                  onChange={(e) =>
                    setSearchData({ ...searchData, arrivalIataCode: e.target.value })
                  }
                >
                  <option value="">Select Arrival Airport</option>
                  {airports
                    .filter((airport) => airport.iataCode !== searchData.departureIataCode)
                    .map((airport) => (
                      <option key={`arr-${airport.iataCode}`} value={airport.iataCode}>
                        {formatAiportOption(airport)}
                      </option>
                    ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="">Departure Date</label>
                <input
                  type="date"
                  required
                  value={searchData.departureDate}
                  onChange={(e) => setSearchData({ ...searchData, departureDate: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            <button className="search-button" type="submit">
              Search Flights
            </button>
          </form>
        </div>
      </div>

      <section className="popular-destinations">
        <h2>Popular Destinations</h2>
        <p>Explore some of the most popular destinations for your next trip.</p>

        <div className="destinations-grid">
          {popularDestinations.map((destination) => (
            <div key={destination.id} className="destination-card">
              <div
                className="destination-image"
                style={{ backgroundImage: `url(/images/${destination.image})` }}
              >
                <div className="destination-overlay">
                  <h3>{destination.city}</h3>
                  <p>{destination.country}</p>
                </div>
                <div className="destination-footer">
                  <span>From {destination.price}</span>
                  <Link to="/flights" className="book-button">
                    Book Now
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="features-section">
        <h2>Why Choose Daniel Airlines</h2>

        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">✈️</div>
            <h3>Modern Fleet</h3>
            <p>Fly with our state-of-the-art aircraft for a comfortable journey.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">⏱️</div>
            <h3>On-Time Performance</h3>
            <p>
              We pride ourselves on punctual departures and arrivals, ensuring you reach your
              destination on time.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🍽️</div>
            <h3>Gourmet Dining</h3>
            <p>Enjoy exquisite meals prepared by top chefs during your flight.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">💺</div>
            <h3>Extra Legroom</h3>
            <p>Experience more space and comfort with our extra legroom seating options.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
